import { getUnixTime, format, isValid } from 'date-fns';

import { TOKEN_KEY, TOKEN_THRESHOLD_KEY, REFRESH_TOKEN_KEY } from './constants';
import { timestampToDate } from './date';
import { generatePath } from 'react-router';
import { createSearchParams } from 'react-router-dom';
import { isDocFile, toast } from '@utilities';
import { type AutoCompleteCompleteEvent } from 'primereact/autocomplete';
import queryString from 'query-string';

export const getAccessToken = (): string => localStorage.getItem(TOKEN_KEY) ?? '';

/**
 * Session is renewable while a refresh token remains. Do not hard-redirect here —
 * the API interceptor refreshes on 401 and only forces login when refresh fails.
 */
export const isLoggedIn = (): boolean => {
	const accessToken = getAccessToken();
	const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY) || '';
	const threshold = Number(localStorage.getItem(TOKEN_THRESHOLD_KEY));

	if (!accessToken && !refreshToken) {
		return false;
	}

	if (!threshold) {
		return !!(accessToken || refreshToken);
	}

	const now = getUnixTime(new Date());
	if (now >= threshold) {
		return !!refreshToken;
	}

	return !!accessToken;
};

export const saveLocale = (locale: string): void => {
	localStorage.setItem('locale', locale);
};

export const setDocumentLang = (locale: string): void => {
	document.documentElement.setAttribute('lang', locale);
};

export const renderAddress = (address: any) => {
	const addr: any = [];

	if (address.addressLine1) addr.push(address.addressLine1);
	if (address.addressLine2) addr.push(address.addressLine2);
	if (address.district) addr.push(address.district);
	if (address.city) addr.push(address.city);
	if (address.state) addr.push(address.state);
	if (address.country) addr.push(address.country);
	if (address.pincode) addr.push(address.pincode);

	return addr.join(', ');
};

export const epochToDateRange = (startDateTimestamp: any, endDateTimestamp: any, type = 'OBJECT') => {
	const startDate = timestampToDate(startDateTimestamp);
	const endDate = timestampToDate(endDateTimestamp);
	return type === 'ARRAY'
		? [startDate || null, endDate || null]
		: { startDate: timestampToDate(startDateTimestamp), endDate: timestampToDate(endDateTimestamp) };
};

export const timestampToFormattedDate = (timestamp: number | string | undefined, dateFormat: string) => {
	return timestamp !== undefined && isValid(timestamp) ? format(new Date(timestamp), dateFormat) : '';
};

export const goBack = (navigate: any) => {
	navigate('..', { relative: 'path' });
};

export const handleEmptyNullValues = (obj: Record<string, any>) => {
	const ignores = [null, undefined, '', [], {}];
	const isNonEmpty = (d: any) => !ignores.includes(d) && (typeof d !== 'object' || Object.keys(d).length);

	return JSON.parse(JSON.stringify(obj), function (k, v) {
		if (isNonEmpty(v)) return v;
	});
};

export const normalizeWarrantyCodes = (codes: unknown): string[] => {
	if (!codes) return [];
	const codeList = Array.isArray(codes) ? codes : [codes];
	return Array.from(
		new Set(
			codeList
				.map(code => {
					if (typeof code === 'string') {
						const separatorIndex = code.lastIndexOf('__');
						return separatorIndex >= 0 ? code.slice(separatorIndex + 2) : code;
					}
					if (code && typeof code === 'object') {
						const record = code as Record<string, unknown>;
						if (typeof record.code === 'string') return record.code;
						if (typeof record.warrantyCode === 'string') return record.warrantyCode;
					}
					return '';
				})
				.filter(Boolean)
		)
	);
};

const QUOTATION_PRODUCT_UI_ONLY_FIELDS = new Set([
	'isToggle',
	'allowProductNameUpdate',
	'areaId',
	'itemId',
	'nickname'
]);

export const stripQuotationProductUiFields = (product: Record<string, any>) => {
	const stripped = { ...product };
	QUOTATION_PRODUCT_UI_ONLY_FIELDS.forEach(field => {
		delete stripped[field];
	});
	return stripped;
};

/** Backend quotation product fields that Jackson binds as String — never send objects. */
export const toQuotationStringField = (
	value: unknown,
	fallback = '',
	nestedKeys: string[] = []
): string => {
	if (typeof value === 'string') return value;
	if (value && typeof value === 'object' && !Array.isArray(value)) {
		const record = value as Record<string, unknown>;
		for (const key of nestedKeys) {
			if (typeof record[key] === 'string') return record[key];
		}
	}
	return fallback;
};

const applyQuotationStringFields = (
	product: Record<string, any>,
	fallbackProduct?: Record<string, any>
) => {
	product.calculation = toQuotationStringField(
		product.calculation,
		toQuotationStringField(fallbackProduct?.calculation)
	);
	product.productSubCategoryName = toQuotationStringField(
		product.productSubCategoryName,
		toQuotationStringField(fallbackProduct?.productSubCategoryName, '', ['subCategoryName']),
		['subCategoryName']
	);
	product.productCategoryName = toQuotationStringField(
		product.productCategoryName,
		toQuotationStringField(fallbackProduct?.productCategoryName, '', ['categoryName']),
		['categoryName']
	);
	return product;
};

export const mergeQuotationProductForUpdate = (
	existingProduct: Record<string, any> | undefined,
	updatedProduct: Record<string, any>
) => {
	// Pure check/uncheck payloads must not rehydrate content fields (especially productName),
	// otherwise the backend treats the update as a rename and marks the product custom.
	if (updatedProduct?.isToggle) {
		return stripQuotationProductUiFields({
			productId: updatedProduct.productId ?? existingProduct?.productId,
			active:
				updatedProduct.active !== undefined ? updatedProduct.active : (existingProduct?.active ?? true),
			areaEnum: updatedProduct.areaEnum ?? existingProduct?.areaEnum,
			areaName: updatedProduct.areaName ?? existingProduct?.areaName,
			floor: updatedProduct.floor ?? existingProduct?.floor,
			...(updatedProduct.remarks !== undefined ? { remarks: updatedProduct.remarks } : {}),
			...(updatedProduct.descope !== undefined ? { descope: updatedProduct.descope } : {})
		});
	}

	// Reorder is arrangement/view only — never rehydrate price/content fields that can
	// version a quote or mark post-design lines as unapproved extras.
	if (updatedProduct?.isReorder) {
		return {
			productId: updatedProduct.productId ?? existingProduct?.productId,
			productOrder: updatedProduct.productOrder ?? existingProduct?.productOrder,
			areaEnum: updatedProduct.areaEnum ?? existingProduct?.areaEnum,
			areaName: updatedProduct.areaName ?? existingProduct?.areaName,
			floor: updatedProduct.floor ?? existingProduct?.floor,
			isReorder: true
		};
	}

	const merged = {
		...(existingProduct || {}),
		...updatedProduct,
		measurement: {
			...(existingProduct?.measurement || {}),
			...(updatedProduct?.measurement || {})
		}
	};

	merged.productId = updatedProduct?.productId ?? existingProduct?.productId;
	merged.price = Math.ceil(
		Number(
			updatedProduct?.price ??
				updatedProduct?.total ??
				existingProduct?.price ??
				existingProduct?.total ??
				0
		) || 0
	);
	merged.total = Math.ceil(
		Number(updatedProduct?.total ?? updatedProduct?.price ?? existingProduct?.total ?? merged.price) || 0
	);
	merged.active =
		updatedProduct?.active !== undefined ? updatedProduct.active : existingProduct?.active ?? true;
	merged.warrantyCodes = normalizeWarrantyCodes(
		updatedProduct?.warrantyCodes ?? existingProduct?.warrantyCodes
	);

	// allowProductNameUpdate is stripped after each merge, so subsequent merges must still
	// keep an already-applied rename when the incoming name differs from the existing one.
	const shouldApplyProductName =
		Boolean(updatedProduct?.allowProductNameUpdate) ||
		(updatedProduct?.productName !== undefined &&
			existingProduct?.productName !== undefined &&
			updatedProduct.productName !== existingProduct.productName);

	if (shouldApplyProductName && updatedProduct?.productName !== undefined) {
		merged.productName = updatedProduct.productName;
	} else if (existingProduct?.productName !== undefined) {
		merged.productName = existingProduct.productName;
	} else if (updatedProduct?.productName !== undefined) {
		merged.productName = updatedProduct.productName;
	}

	return stripQuotationProductUiFields(applyQuotationStringFields(merged, existingProduct));
};

export const restoreCleanedQuotationProduct = (
	cleanedProduct: Record<string, any>,
	originalProduct: Record<string, any>
) => {
	if (!originalProduct) return cleanedProduct;

	const hasMeasurement =
		(originalProduct.measurement && Object.keys(originalProduct.measurement).length > 0) ||
		(cleanedProduct.measurement && Object.keys(cleanedProduct.measurement).length > 0);

	return stripQuotationProductUiFields(
		applyQuotationStringFields(
			{
				...originalProduct,
				...cleanedProduct,
				...(hasMeasurement
					? {
							measurement: {
								...(originalProduct.measurement || {}),
								...(cleanedProduct.measurement || originalProduct.measurement || {})
							}
						}
					: {}),
				warrantyCodes: normalizeWarrantyCodes(
					cleanedProduct.warrantyCodes ?? originalProduct.warrantyCodes
				)
			},
			originalProduct
		)
	);
};

export const navigateTo = (navigate: any, path: any, pathParams: any, queryParams?: any, options: any = {}) => {
	const navigationPath = generatePath(path, pathParams);
	navigate({
		...options,
		pathname: navigationPath,
		search: createSearchParams(queryParams).toString()
	});
};

export const searchAction = (dispatch: any, event: AutoCompleteCompleteEvent, action: any) => {
	const prefix = event.query;

	dispatch({
		type: action,
		payload: {
			prefix,
			errorCB: (message: string) => {
				toast('ERROR', message);
			}
		}
	});
};

export const transformFullName = (obj: any, firstNameKey: string, lastNameKey: string, phoneNumberKey?: string) => {
	const firstName = obj?.[firstNameKey] ? obj[firstNameKey] : '';
	const lastName = obj?.[lastNameKey] ? obj[lastNameKey] : '';
	const phoneNumber = obj && phoneNumberKey && obj[phoneNumberKey] ? obj[phoneNumberKey] : '';
	return `${firstName} ${lastName} ${phoneNumber ? `(${phoneNumber})` : ''}`.trim();
};

export const sanitizeValue = (value: unknown) => {
	return value.replace(/\s+/g, ' ').replace(/\n+/g, ' ').trim();
};

/**
 * Sanitizes a string to only contain ISO-8859-1 compatible characters
 * Removes any characters that cannot be encoded in ISO-8859-1
 */
export const sanitizeToISO88591 = (str: string): string => {
	if (typeof str !== 'string') return str;
	// Remove any character that is not in ISO-8859-1 range (0x00-0xFF)
	return str
		.split('')
		.map(char => {
			const code = char.charCodeAt(0);
			// ISO-8859-1 range: 0x00-0xFF (0-255)
			return code <= 255 ? char : '';
		})
		.join('');
};

/**
 * Recursively sanitizes all string values in an object to be ISO-8859-1 compatible
 */
export const sanitizeObjectStrings = (obj: Record<string, any>): any => {
	if (obj === null || obj === undefined) return obj;

	if (typeof obj === 'string') {
		return sanitizeToISO88591(obj);
	}

	if (Array.isArray(obj)) {
		return obj.map(item => sanitizeObjectStrings(item));
	}

	if (typeof obj === 'object') {
		const sanitized: any = {};
		for (const key in obj) {
			if (Object.prototype.hasOwnProperty.call(obj, key)) {
				sanitized[key] = sanitizeObjectStrings(obj[key]);
			}
		}
		return sanitized;
	}

	return obj;
};

export const handleSortingValues = (obj: any, sortEnum: any) => {
	let newObj = { ...obj };
	switch (obj.sortOrder) {
		case 1:
			newObj = { ...obj, sortByOrder: 'ASC', sortByField: sortEnum[obj.sortField] };
			delete newObj.sortField;
			delete newObj.sortOrder;
			break;
		case -1:
			newObj = { ...obj, sortByOrder: 'DESC', sortByField: sortEnum[obj.sortField] };
			delete newObj.sortField;
			delete newObj.sortOrder;
			break;
		default:
			newObj = { ...obj };
	}

	return newObj;
};

export const convertSearchParamsToFilters = (searchParams: any, FILTERS: any) => {
	const filters: any = {};
	const parsed = queryString.parse(searchParams.toString(), { arrayFormat: 'bracket' });

	Object.keys(parsed).forEach((key: Record<string, any>) => {
		const filterConfig = FILTERS[key];
		if (filterConfig) {
			if (FILTERS[key]?.type == 'DateRange') {
				const range = (parsed[key] as string[]).map((v: string) => {
					return !!v ? parseInt(v, 10) : null;
				});
				filters[key] = epochToDateRange(range[0], range[1], 'ARRAY');
			} else if (filterConfig.type === 'Array' && filterConfig.item === 'Object') {
				const values = parsed[key];

				if (Array.isArray(values)) {
					filters[key] = values.map((val: Record<string, any>) => {
						return (
							filterConfig.options?.find((option: Record<string, any>) => option.value === val) || {
								value: val,
								label: val
							}
						);
					});
				} else {
					filters[key] = [
						filterConfig.options?.find((option: Record<string, any>) => option.value === values) || {
							value: values,
							label: values
						}
					];
				}
			} else if (filterConfig.type === 'Autocomplete') {
				const values = parsed[key];

				if (Array.isArray(values)) {
					filters[key] = values.map((val: Record<string, any>) => JSON.parse(val));
				} else {
					filters[key] = [JSON.parse(values as string)];
				}
			} else {
				filters[key] = parsed[key];
			}
		}
	});

	return filters;
};

export const convertFiltersToSearchParams = (selectedFilters: any, setSearchParam: any, FILTERS: any) => {
	const cleanedFilters = handleEmptyNullValues(selectedFilters);
	if (cleanedFilters == null) {
		setSearchParam('');
		return '';
	}
	const filters: any = {};
	Object.keys(cleanedFilters).forEach((key: Record<string, any>) => {
		if (FILTERS[key]?.type === 'DateRange') {
			const startDate = cleanedFilters[key][0];
			const endDate = cleanedFilters[key][1];
			filters[key] = [new Date(startDate)?.getTime() ?? null, new Date(endDate)?.getTime() ?? null];
		} else if (FILTERS[key]?.type === 'Object') {
			filters[key] = JSON.stringify(cleanedFilters[key]);
		} else if (FILTERS[key]?.type === 'Array' && FILTERS[key].item === 'Object') {
			filters[key] = cleanedFilters[key].map((filterObj: Record<string, any>) => filterObj.value || filterObj.key);
		} else if (FILTERS[key]?.type === 'Autocomplete') {
			filters[key] = cleanedFilters[key].map((obj: Record<string, any>) => {
				return JSON.stringify(obj);
			});
		} else {
			filters[key] = cleanedFilters[key];
		}
	});
	const searchParams = queryString.stringify(filters, { arrayFormat: 'bracket' });
	setSearchParam(searchParams);
	return searchParams;
};

export const isShallowEqual = (v: { [x: string]: any }, o: { [x: string]: any }) => {
	for (const key in v) if (!(key in o) || v[key] !== o[key]) return false;
	for (const key in o) if (!(key in v) || o[key] !== v[key]) return false;
	return true;
};

export const navigateToExternalLink = (url: string) => {
	window.location.href = url;
};

export function transformUploadData(values: Record<string, any>, currentFileType: any) {
	const transformedData = {
		document: [
			{
				...values,
				// files: values.files.map((file: Record<string, any>) => {
				files: (Array.isArray(values.files) ? values.files : values.files.files).map((file: Record<string, any>) => {
					return {
						fileName: file.name,
						fileType: currentFileType,
						...(isDocFile(file, file.type) || isDocFile(file.type) ? {} : { thumbnailFileName: file.name })
						// link: FILE_TYPE_KEYS.LINK && file.link
					};
				})
			}
		]
	};

	return transformedData;
}

const resolveDownloadFileName = (fileUrl: string, fileName?: string) => {
	if (fileName?.trim()) return fileName.trim();
	try {
		const path = new URL(fileUrl).pathname;
		const fromPath = decodeURIComponent(path.split('/').pop() || '');
		if (fromPath) return fromPath;
	} catch {
		// ignore invalid URL parse
	}
	const fallback = String(fileUrl).split('?')[0].split('/').pop();
	return fallback || 'download';
};

/** Download a file from a (often signed S3) URL. Falls back when CORS blocks fetch. */
export const handleDownload = async (fileUrl?: string, fileName?: string) => {
	if (!fileUrl) {
		toast('ERROR', 'Download URL is missing');
		return;
	}

	const resolvedName = resolveDownloadFileName(fileUrl, fileName);
	const triggerAnchorDownload = (href: string, downloadName?: string) => {
		const link = document.createElement('a');
		link.href = href;
		if (downloadName) link.download = downloadName;
		link.target = '_blank';
		link.rel = 'noopener noreferrer';
		document.body.appendChild(link);
		link.click();
		link.remove();
	};

	try {
		const response = await fetch(fileUrl, { mode: 'cors' });
		if (!response.ok) throw new Error('Network response was not ok');

		const blob = await response.blob();
		const objectUrl = window.URL.createObjectURL(blob);
		triggerAnchorDownload(objectUrl, resolvedName);
		window.URL.revokeObjectURL(objectUrl);
	} catch {
		// Signed S3 URLs often block cross-origin fetch; open/download via direct link instead
		try {
			triggerAnchorDownload(fileUrl, resolvedName);
		} catch {
			toast('ERROR', 'Failed to download the file. Please try again.');
		}
	}
};

export const renderFileUploadControl = () => {
	const accept = 'image/*,application/pdf';
	const showUploadControl = true;
	return {
		accept,
		showUploadControl
	};
};

export const calculateTotal = product => {
	const { unitOfMeasure, calculation, measurement, unitCost, quantity, builder, price } = product;

	if (builder === 'FC' || builder === 'CT') {
		return price;
	} else if (unitOfMeasure === 'SQFT') {
		const variables = {
			W: measurement?.width,
			H: measurement?.height,
			D: measurement?.depth,
			Area: measurement?.area,
			R: unitCost
		};
		let total;
		try {
			total = eval(calculation?.replace(/[A-Za-z]+/g, match => variables[match])) || 0;
		} catch (e) {
			total = 0;
		}

		return (total * quantity) / 92903.04;
	} else {
		return product.unitCost * quantity;
	}
};

export const getEnumDisplayValue = (
	enumValue: string | undefined | null,
	enumMapping: Record<string, { label: string }>
): string => {
	if (!enumValue || !enumMapping) {
		return enumValue || '';
	}
	return enumMapping[enumValue]?.label || enumValue;
};

export const displayTextToEnum = text => {
	if (typeof text !== 'string') {
		throw new TypeError('Expected a string');
	}

	return text
		.trim()
		.replace(/[^a-zA-Z0-9\s]/g, '')
		.replace(/\s+/g, '_')
		.toUpperCase();
};
